import {
  getBasicTextColor,
  getBasicBackgroundColor,
  getOppositeTextColor,
} from '../../../core/utils/view';
import { Freeze, Area, Viewport } from '../../types';
import {
  doAreasIntersect,
  convertRowFreezeRangeToArea,
  convertColFreezeRangeToArea,
} from '../../utils/area/area';
import {
  IterateViewportParams,
  getViewportStartEnd,
  iterateViewportBackward,
  iterateViewportForward,
} from '../../utils/views/virtualViewport';
import { RowCellsProps } from './cellViewTypes';
import Color from 'tinycolor2';

export const doAreaIntersectWithViewport = (
  freezedCols: Freeze,
  freezedRows: Freeze,
  area: Area,
  vAreas: ReturnType<typeof generateCombinedViewportAreas>
) =>
  doAreasIntersect(area, vAreas.nonFreezeViewportArea) ||
  (freezedRows.frozen && doAreasIntersect(area, vAreas.frozenRowArea)) ||
  (freezedCols.frozen && doAreasIntersect(area, vAreas.frozenColArea)) ||
  (vAreas.collectiveFreezeArea &&
    doAreasIntersect(area, vAreas.collectiveFreezeArea));

export const generateCombinedViewportAreas = (
  colViewport: Viewport,
  rowViewport: Viewport,
  freezedCols: Freeze,
  freezedRows: Freeze
) => {
  const row = getViewportStartEnd(rowViewport.nonFrozenIndexes);
  const freezeRow = getViewportStartEnd(rowViewport.frozenIndexes);

  const col = getViewportStartEnd(colViewport.nonFrozenIndexes);
  const freezeCol = getViewportStartEnd(colViewport.frozenIndexes);

  const nonFreezeViewportArea: Area = {
    start: {
      ri: row.start,
      ci: col.start,
    },
    end: {
      ri: row.end,
      ci: col.end,
    },
  };

  const colFreezeRange = getViewportStartEnd(colViewport.frozenIndexes);
  const rowFreezeRange = getViewportStartEnd(rowViewport.frozenIndexes);

  const frozenRowArea = convertRowFreezeRangeToArea(
    rowFreezeRange,
    nonFreezeViewportArea
  );
  const frozenColArea = convertColFreezeRangeToArea(
    colFreezeRange,
    nonFreezeViewportArea
  );

  let collectiveFreezeArea: Area | undefined;
  if (freezedRows.frozen && freezedCols.frozen) {
    collectiveFreezeArea = {
      start: {
        ci: freezeCol.start,
        ri: freezeRow.start,
      },
      end: {
        ci: freezeCol.end,
        ri: freezeRow.end,
      },
    };
  }

  return {
    nonFreezeViewportArea,
    frozenRowArea,
    frozenColArea,
    collectiveFreezeArea,
  };
};

// #region get merge cells dimensions + helpers
export const getMergedCellDimensions = (
  props: RowCellsProps,
  { start, end }: Area,
  currentRowIndex: number,
  rowY: number,
  initialCi: number,
  initialX: number
  //   colParams: Pick<ColumnCellView, 'ci' | 'x'>,
) => {
  const {
    colSizes,
    defaultColWidth,
    zoom,
    isRtl,
    rowSizes,
    defaultRowHeight,
    filterResults,
    hiddenRows,
    hiddenCols,
    freezedRows,
  } = props;

  //   Get X and width
  let width = 0;
  let x = initialX;

  // console.log(start.ci, end.ci);

  // console.log('colParams.x', x);
  const colCB = ({ size, index }: IterateViewportParams) => {
    // console.log('index', index);
    if (isRtl) {
      if (index > initialCi) {
        x -= size;
      }
    } else if (index < initialCi) {
      x -= size;
      // console.log("index", index, size, x)
    }
    width += size;

    return false;
  };

  iterateViewportForward(
    start.ci,
    end.ci,
    colSizes,
    hiddenCols,
    colCB,
    freezedRows,
    defaultColWidth,
    zoom
  );

  //   Get Y and height
  let height = 0;
  let totalVisibleRowsInMerge = 0;
  let y = rowY;

  let isCurrentIndexValidated = false;
  const rowCB = ({ size, index }: IterateViewportParams) => {
    if (index > end.ri || index < start.ri) return true;

    if (index === currentRowIndex) {
      if (isCurrentIndexValidated) return false;

      isCurrentIndexValidated = true;
    }

    if (start.ri < currentRowIndex && index < currentRowIndex) {
      y -= size;
    }

    height += size;
    totalVisibleRowsInMerge += 1;
    return false;
  };

  // We are using backward and forward for row because in case of row we may have filter applied
  // Therefore we'll jump from filter to filter
  iterateViewportBackward(
    start.ri,
    currentRowIndex,
    rowSizes,
    hiddenRows,
    rowCB,
    freezedRows,
    defaultRowHeight,
    zoom,
    filterResults
  );
  iterateViewportForward(
    currentRowIndex,
    end.ri,
    rowSizes,
    hiddenRows,
    rowCB,
    freezedRows,
    defaultRowHeight,
    zoom,
    filterResults
  );

  return {
    x,
    y,
    width,
    height,
    totalVisibleRowsInMerge,
  };
};
// #endregion

export const getTextContrastColor = (
  backgroundColor: string | null | undefined,
  color: string | null | undefined,
  isDarkTheme: boolean
) => {
  const textColor = getBasicTextColor(isDarkTheme);
  const secondaryColor =
    backgroundColor || getBasicBackgroundColor(isDarkTheme);

  let primaryColor = color || textColor;
  // ? mostReadable(backgroundColor, [ligh]) //getContrastTextColor(backgroundColor)
  // : textColor;

  if (!color) {
    const baseColor = backgroundColor || secondaryColor;
    const lightColor = Color(baseColor).lighten(35);
    const darkColor = Color(baseColor).darken(35);
    primaryColor = Color.mostReadable(baseColor, [lightColor, darkColor], {
      includeFallbackColors: false,
    }).toHexString();
  }

  // const opposite = Color(primaryColor)
  return {
    primaryColor,
    secondaryColor,
    // oppositeColor: getOppositeTextColor(isDarkTheme),
  };
};
